export type Birthdate = {
  year: number;
  month: number;
  day: number;
};

export type KycSchemaVersion = 1 | 2;

export const taxBracketOptions = [
  { label: '10%', value: 0.10 },
  { label: '12%', value: 0.12 },
  { label: '22%', value: 0.22 },
  { label: '24%', value: 0.24 },
  { label: '32%', value: 0.32 },
  { label: '35%', value: 0.35 },
  { label: '37%', value: 0.37 },
];

const timeHorizonMidpoints: Record<string, number> = {
  'Under 1 year': 0.5,
  '1-2 years': 1.5,
  '3-5 years': 4,
  '6-10 years': 8,
  '11-20 years': 15.5,
  'Over 20 years': 25,
};

export const parseTaxBracketValue = (taxBracketLabel?: string): number | undefined => {
  if (!taxBracketLabel) return undefined;
  const trimmed = taxBracketLabel.trim().replace('%', '');
  const parsed = Number(trimmed);
  if (Number.isNaN(parsed)) return undefined;
  return parsed / 100;
};

export const deriveRetirementYear = (timeHorizonLabel?: string, baseYear = new Date().getFullYear()): number | undefined => {
  if (!timeHorizonLabel) return undefined;
  const midpoint = timeHorizonMidpoints[timeHorizonLabel];
  if (midpoint === undefined) return undefined;
  return Math.round(baseYear + midpoint);
};

export const parseBirthdateFromDob = (dob?: string): Birthdate | undefined => {
  if (!dob) return undefined;
  const [yearStr, monthStr, dayStr] = dob.split('-');
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);
  if (!year || !month || !day) return undefined;
  return { year, month, day };
};

export const calculateAgeFromBirthdate = (birthdate?: Birthdate, today = new Date()): number | undefined => {
  if (!birthdate) return undefined;
  const { year, month, day } = birthdate;
  if (!year || !month || !day) return undefined;
  const dob = new Date(year, month - 1, day);
  if (Number.isNaN(dob.getTime())) return undefined;

  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }

  const daysInYear = ((today.getFullYear() % 4 === 0 && today.getFullYear() % 100 !== 0) || today.getFullYear() % 400 === 0) ? 366 : 365;
  const dayOfYear = Math.floor((today.getTime() - new Date(today.getFullYear(), 0, 0).getTime()) / (1000 * 60 * 60 * 24));
  const dobDayOfYear = Math.floor((dob.getTime() - new Date(dob.getFullYear(), 0, 0).getTime()) / (1000 * 60 * 60 * 24));
  const fractionalYear = (dayOfYear - dobDayOfYear) / daysInYear;

  return Math.round((age + fractionalYear) * 100) / 100;
};

export const calculateTimeHorizonYears = (retirementYear?: number, baseYear = new Date().getFullYear()): number | undefined => {
  if (!retirementYear) return undefined;
  return retirementYear - baseYear;
};

export const migrateKycToV2 = (client: Record<string, any>): Record<string, any> => {
  const schemaVersion = client.schemaVersion ?? 1;
  if (schemaVersion >= 2) {
    return client;
  }

  const retirementYear = deriveRetirementYear(client.timeHorizon);
  const birthdate = parseBirthdateFromDob(client.dob);
  const taxBracketValue = parseTaxBracketValue(client.taxBracket);

  return {
    ...client,
    schemaVersion: 2,
    retirementYear: client.retirementYear ?? retirementYear,
    birthdate: client.birthdate ?? birthdate,
    taxBracketValue: client.taxBracketValue ?? taxBracketValue,
  };
};
